import chainPrompt from "@/modules/chainPrompt";
import config from "@/modules/config";
import { isLastMethodStep } from "@/modules/methodStep";
import { isChainRevealed } from "@/modules/chainReveal";
import { shopWindowTypes, valueNames } from "@/modules/Constants";
import type ShopWindowConfig from "@/plugin/ShopWindowConfig";
import type { ChainStatus } from "@/stores/chain/types";
import type { ChainRecommendation } from "@/types/experience";
import type { ExperienceState } from "@/types/state";

export type NextVideoBlockProps = {
  experienceState: ExperienceState;
  variant?: string;
  compact?: boolean;
  lastStepPrompt?: boolean;
  onApplicationEvent?: (event: Record<string, unknown>) => void;
};

export type ResolvedProps = NextVideoBlockProps & { variant: string };

export interface BlockState {
  chain: ChainRecommendation[];
  status: ChainStatus;
  dismissed: boolean;
  // Fixed countdown value while counting; null otherwise.
  secondsLeft: number | null;
  // Times the countdown reached zero; the commit that changes it runs the advance.
  zeroCount: number;
}

export function experienceIdOf(props: ResolvedProps) {
  const experienceData = props.experienceState.experienceData;
  return experienceData ? experienceData.idexperience : null;
}

// Single source of truth for "should this block be visible": it decides both
// the render and the impression event.
export function visiblePick(props: ResolvedProps, state: BlockState) {
  // Consult the shared flag too: on a tab-switch remount the local `dismissed`
  // is still false until the first sync, so a cancelled prompt stays hidden.
  if (
    state.dismissed ||
    chainPrompt.isDismissed(experienceIdOf(props)) ||
    state.status !== "ready"
  ) {
    return null;
  }
  const pick = (state.chain || [])[0];
  if (!pick) {
    return null;
  }
  const applicationData = props.experienceState.applicationData;
  const experienceData = props.experienceState.experienceData;
  const revealed = isChainRevealed(applicationData);
  const inline = props.variant === "inline";
  const isPortrait = applicationData.isPortrait();

  const activeConfig = applicationData.getValue(
    valueNames.SHOP_WINDOW_CONFIG,
  ) as ShopWindowConfig | null | undefined;
  const onLastMethodStep =
    activeConfig &&
    activeConfig.type === shopWindowTypes.METHOD &&
    isLastMethodStep(
      activeConfig,
      experienceData,
      config.getVisibleMethodSteps(),
    );

  if (inline) {
    // The inline instance also carries the pre-reveal prompt on the last method
    // step: in portrait, and in immersive landscape when `lastStepPrompt` is set.
    const showEarly = (isPortrait || props.lastStepPrompt) && onLastMethodStep;
    if (!revealed && !showEarly) {
      return null;
    }
  } else if (revealed || isPortrait || !onLastMethodStep) {
    // The overlay shows the early prompt in landscape only, and yields once revealed.
    return null;
  }

  return pick;
}
